import { KeyboardEvent, useEffect, useState } from 'react';

import { Chip, FormControl, TextField } from '@mui/material';

import { ConditionalTooltip } from 'shared/components/Conditional';
import { generateRandomString } from 'utils/string';

import './InputTextFieldChips.scss';

import { stringify } from 'querystring';

export type ExternalErrorOnItemsT = {
  message: string;
  // eslint-disable-next-line no-unused-vars
  findItemFn(localItem: any): boolean;
}
// eslint-disable-next-line no-unused-vars
type FnStringT = (str: string) => void;
interface InputTextFieldChipsProps {
  name: string;
  defaultValues: any[];
  // eslint-disable-next-line no-unused-vars
  formatItem: (input: string, setError: FnStringT) => any | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (newdata: any) => void;
  // eslint-disable-next-line no-unused-vars
  formatLabel: (item: any) => string;
  externalError?: string; // External error
  externalErrorOnItems?: ExternalErrorOnItemsT[];
  children?: any;
  forceRerender?: number;
  defaultHelperText?: string;
  sx?: any;
  fullWidth?: any;
}
function InputTextFieldChips({
  name,
  defaultValues = [],
  formatItem,
  onChange,
  formatLabel,
  externalError,
  children,
  externalErrorOnItems,
  forceRerender,
  defaultHelperText = 'Press enter to add value',
  sx,
  fullWidth,
}: InputTextFieldChipsProps) {
  const [input, setInput] = useState('');
  const [list, setList] = useState<any[]>(defaultValues);
  const [error, setError] = useState('');
  useEffect(() => {
    setList(defaultValues);
  }, [defaultValues, forceRerender]);

  const alreadyExists = (newItem: any) => {
    if (typeof newItem === 'object') {
      return list.some((item) => stringify(item) === stringify(newItem));
    }
    return list.indexOf(newItem) !== -1;
  };

  const onPressEnter = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.code === 'Enter' && input) {
      e.preventDefault();
      const newItem = formatItem(input, setError);
      if (alreadyExists(newItem)) {
        setError('Already exists');
        return;
      }
      if (newItem === null) {
        return;
      }
      let tmpList = list;
      if (!tmpList) {
        tmpList = [];
      }
      tmpList.push(newItem);
      setList(tmpList);
      setInput('');
      setError('');
      onChange(list);
    }
  };

  const onDelete = (index: number) => {
    const tmpList = list;
    tmpList.splice(index, 1);
    // Allows to rerender the array
    setList([...tmpList]);
    onChange(tmpList);
  };

  return (
    <div>
      <div className={`${children ? 'actions-side-by-side' : ''}`}>
        <FormControl fullWidth={fullWidth} sx={sx}>
          <TextField
            label={name}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={onPressEnter}
            error={!!error || !!externalError}
            helperText={
              (!list.length && !error && !externalError)
                ? defaultHelperText
                : error || externalError
            }
          />
        </FormControl>
        { children && children }
      </div>
      <div>
        {
          list.map((item, index) => {
            const isError = externalErrorOnItems?.find(
              (externalE) => externalE.findItemFn(item),
            );
            return (
              <ConditionalTooltip
                key={generateRandomString()}
                title={isError ? `Error: ${isError.message}` : undefined}
              >
                <Chip
                  key={generateRandomString()}
                  label={formatLabel(item)}
                  sx={{ m: 0.5 }}
                  onDelete={() => onDelete(index)}
                  color={isError ? 'error' : undefined}
                />
              </ConditionalTooltip>
            );
          })
        }
      </div>
    </div>
  );
}

export default InputTextFieldChips;
